/**
 * ThemeProvider — loads the embed-config (live /api/public/embed-config via the api facade, or the
 * mock), applies the tenant accent + fonts to :root, and exposes the resolved town brand AND the
 * active category list to the tree. This is where theming.md's layers are wired together at runtime;
 * the categories the API returns drive the intake radio-cards.
 *
 * MVP: there is NO light/dark toggle. The whole app — staff console AND resident forms — renders in
 * the town's ONE config-branded LIGHT look (theming.md "branded, no toggle"). We keep applying the
 * `"light"` ramp so the accent/fonts inherit from the town config, and never write data-theme=dark.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { EmbedConfig } from "./embedConfig.js";
import { DEFAULT_EMBED_CONFIG } from "./mockEmbedConfig.js";
import { applyMode, applyTenant } from "./applyTheme.js";
import { useBrandFontStacks } from "./fonts.js";
import { fetchEmbedConfig } from "../api/index.js";
import { MOCK_CATEGORIES, type IntakeCategory } from "../api/categories.js";
import { MOCK_QUESTION_TOPICS, type QuestionTopic } from "../api/questionTopics.js";
import { MOCK_CHANNELS } from "../api/channels.js";
import { MOCK_KORA_RECORD_TYPES, type KORARecordType } from "../api/koraRecordTypes.js";
import { MOCK_PERMIT_TYPES, type PermitType } from "../api/permitTypes.js";
import { DEFAULT_KORA_CONFIG, type KORAEmbedConfig } from "../api/embedConfigMap.js";
import type { ChannelView } from "../api/types.js";

interface ThemeContextValue {
  config: EmbedConfig;
  categories: IntakeCategory[];
  /** Active Kind=question Category rows (ask-a-question-faq.md §2.1) — the live topic taxonomy for
   *  AskPage's topic picker and FaqPage's topic filter. Falls back to MOCK_QUESTION_TOPICS the same
   *  way `categories` falls back to MOCK_CATEGORIES: on fetch failure or an empty live list. */
  questionTopics: QuestionTopic[];
  /** Active Kind=channel Category rows (stay-informed-channels.md §2.1) — the "what updates do you
   *  want?" checkbox list for SubscribePage + PreferencesPage. Falls back to MOCK_CHANNELS the same
   *  way `categories`/`questionTopics` fall back on fetch failure or an empty live list. */
  channels: ChannelView[];
  /** Active Kind=kora Category rows (records-request.md) — the record-type taxonomy for
   *  RecordsRequestPage's optional record-type picker. Falls back to MOCK_KORA_RECORD_TYPES the
   *  same way the other taxonomies fall back on fetch failure or an empty live list. */
  koraRecordTypes: KORARecordType[];
  /** Active Kind=permit Category rows (Permits & Licensing, roadmap module 6) — the permit-type
   *  taxonomy for ApplyForPermitPage's type picker. Falls back to MOCK_PERMIT_TYPES the same way the
   *  other taxonomies fall back on fetch failure or an empty live list. */
  permitTypes: PermitType[];
  /** State-configurable Open Records response-clock length + citation label (records-request.md
   *  "STATE-CONFIGURABLE, not hardcoded law") — ships the Kansas KORA profile as the default. */
  kora: KORAEmbedConfig;
  /** Council Agenda + Packet public-display toggles (meetings-public-relaunch.md §3) — resolved
   *  booleans, always true/false, default true. The server already OMITS narrativeHtml/attachments
   *  from the public meeting detail when false; these mainly let MeetingsPage/PublicMeetingView
   *  degrade layout cleanly (e.g. never render an empty "Attachments" affordance). */
  publicShowAgendaItemDetails: boolean;
  publicShowAttachments: boolean;
  loading: boolean;
  /** Update the shared brand config in place (e.g. after a Branding admin save) so the console
   *  chrome (StaffShell/TownHeader breadcrumb, live preview) reflects the new town name/accent/
   *  background/logo/fonts immediately — no full page reload required (#170). Callers pass a
   *  PARTIAL patch; unspecified fields keep their current value. */
  applyConfig: (patch: Partial<EmbedConfig>) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  children,
  initialConfig,
  initialCategories,
  initialQuestionTopics,
  initialChannels,
  initialKORARecordTypes,
  initialPermitTypes,
  initialKORAConfig,
}: {
  children: ReactNode;
  /** Test/story override so a fixed config can be injected without the async fetch. */
  initialConfig?: EmbedConfig;
  initialCategories?: IntakeCategory[];
  initialQuestionTopics?: QuestionTopic[];
  initialChannels?: ChannelView[];
  initialKORARecordTypes?: KORARecordType[];
  initialPermitTypes?: PermitType[];
  initialKORAConfig?: KORAEmbedConfig;
}): JSX.Element {
  const [config, setConfig] = useState<EmbedConfig>(initialConfig ?? DEFAULT_EMBED_CONFIG);
  const [categories, setCategories] = useState<IntakeCategory[]>(
    initialCategories ?? MOCK_CATEGORIES,
  );
  const [questionTopics, setQuestionTopics] = useState<QuestionTopic[]>(
    initialQuestionTopics ?? MOCK_QUESTION_TOPICS,
  );
  const [channels, setChannels] = useState<ChannelView[]>(initialChannels ?? MOCK_CHANNELS);
  const [koraRecordTypes, setKoraRecordTypes] = useState<KORARecordType[]>(
    initialKORARecordTypes ?? MOCK_KORA_RECORD_TYPES,
  );
  const [permitTypes, setPermitTypes] = useState<PermitType[]>(
    initialPermitTypes ?? MOCK_PERMIT_TYPES,
  );
  const [kora, setKora] = useState<KORAEmbedConfig>(initialKORAConfig ?? DEFAULT_KORA_CONFIG);
  const [publicShowAgendaItemDetails, setPublicShowAgendaItemDetails] = useState(true);
  const [publicShowAttachments, setPublicShowAttachments] = useState(true);
  const [loading, setLoading] = useState(!initialConfig);

  // Self-hosted-or-system font stacks for the tenant's chosen fonts (theme/fonts.ts) — starts at
  // the system stack and upgrades once the matching Fontsource webfont CSS resolves.
  const fontStacks = useBrandFontStacks(config.fonts);

  // Layer 3: MVP has no toggle — always pin the LIGHT ramp, and apply the town's accent + fonts on
  // top of it so the whole app inherits the config branding.
  useEffect(() => {
    applyMode("light");
    applyTenant(config, "light", fontStacks);
  }, [config, fontStacks]);

  // Layer 2: fetch the town brand + categories once (skipped when a config is injected).
  useEffect(() => {
    if (initialConfig) return;
    let live = true;
    fetchEmbedConfig()
      .then(
        ({
          config: cfg,
          categories: cats,
          questionTopics: topics,
          channels: chans,
          koraRecordTypes: koraTypes,
          permitTypes: permitTypeList,
          kora: koraCfg,
          publicShowAgendaItemDetails: showAgendaItemDetails,
          publicShowAttachments: showAttachments,
        }) => {
          if (!live) return;
          setConfig(cfg);
          if (cats.length) setCategories(cats);
          if (topics.length) setQuestionTopics(topics);
          if (chans.length) setChannels(chans);
          if (koraTypes.length) setKoraRecordTypes(koraTypes);
          if (permitTypeList.length) setPermitTypes(permitTypeList);
          setKora(koraCfg);
          setPublicShowAgendaItemDetails(showAgendaItemDetails);
          setPublicShowAttachments(showAttachments);
        },
      )
      .catch(() => {
        /* keep the brand default + built-in categories/topics on failure — never a broken form */
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [initialConfig]);

  const applyConfig = (patch: Partial<EmbedConfig>) => {
    setConfig((c) => ({ ...c, ...patch }));
  };

  const value = useMemo<ThemeContextValue>(
    () => ({
      config,
      categories,
      questionTopics,
      channels,
      koraRecordTypes,
      permitTypes,
      kora,
      publicShowAgendaItemDetails,
      publicShowAttachments,
      loading,
      applyConfig,
    }),
    [
      config,
      categories,
      questionTopics,
      channels,
      koraRecordTypes,
      permitTypes,
      kora,
      publicShowAgendaItemDetails,
      publicShowAttachments,
      loading,
    ],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
